import { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import waitTimeManager from '../services/WaitTimeManager';
import {
  startWaitTimeManager,
  stopWaitTimeManager,
  updateWaitTimeData,
  triggerWaitTimeCalculation,
  selectWaitTimes,
  selectNextUnavailable,
  selectWaitTimeMetadata,
  selectIsManagerActive,
  selectWaitTimeLoading,
  selectWaitTimeError,
  selectLastCalculationTime
} from '../redux/slices/waitTimeSlice';

export const useWaitTimeManager = () => {
  const dispatch = useDispatch();
  
  // Selektoren für aktuellen State
  const waitTimes = useSelector(selectWaitTimes);
  const nextUnavailable = useSelector(selectNextUnavailable);
  const metadata = useSelector(selectWaitTimeMetadata);
  const isActive = useSelector(selectIsManagerActive);
  const isLoading = useSelector(selectWaitTimeLoading);
  const error = useSelector(selectWaitTimeError);
  const lastCalculationTime = useSelector(selectLastCalculationTime);
  
  // Abhängige Daten aus AppDataManager (globales System mit Activities)
  const shiftsData = useSelector(state => state.appData.shifts);
  const activities = useSelector(state => state.appData.activities || []);
  const vehicles = useSelector(state => state.appData.vehicles || []);

  // Manager starten
  const startManager = useCallback(async () => {
    try {
      const unsubscribe = await dispatch(startWaitTimeManager());
      return unsubscribe;
    } catch (error) {
      console.error('useWaitTimeManager: Fehler beim Start:', error);
      throw error;
    }
  }, [dispatch]);
  
  // Manager stoppen
  const stopManager = useCallback(async () => {
    try {
      await dispatch(stopWaitTimeManager());
    } catch (error) {
      console.error('useWaitTimeManager: Fehler beim Stopp:', error);
      throw error;
    }
  }, [dispatch]);
  
  // Daten aktualisieren
  const updateData = useCallback((customShiftsData, customActivities, customVehicles, currentDate) => {
    dispatch(updateWaitTimeData(
      customShiftsData || shiftsData,
      customActivities || activities,
      customVehicles || vehicles,
      currentDate
    ));
  }, [dispatch, shiftsData, activities, vehicles]);
  
  // Manuelle Berechnung triggern
  const triggerCalculation = useCallback(async () => {
    try {
      await dispatch(triggerWaitTimeCalculation());
    } catch (error) {
      console.error('useWaitTimeManager: Fehler bei manueller Berechnung:', error);
      throw error;
    }
  }, [dispatch]);
  
  // Konvertiere rohe rides zu Activity-Format für WaitTimeManager
  const convertRidesToActivities = useCallback((rides) => {
    if (!rides || !Array.isArray(rides)) return [];
    
    return rides.map(ride => {
      // Bestimme startTime basierend auf Status und Zeitstempeln
      let startTime = null;
      if (ride.acceptedTimestamp) {
        // Firebase Timestamp handling
        if (ride.acceptedTimestamp.toDate) {
          startTime = ride.acceptedTimestamp.toDate();
        } else if (ride.acceptedTimestamp.seconds) {
          startTime = new Date(ride.acceptedTimestamp.seconds * 1000);
        } else {
          startTime = new Date(ride.acceptedTimestamp);
        }
      } else if (ride.scheduledTime) {
        if (ride.scheduledTime.toDate) {
          startTime = ride.scheduledTime.toDate();
        } else if (ride.scheduledTime.seconds) {
          startTime = new Date(ride.scheduledTime.seconds * 1000);
        } else {
          startTime = new Date(ride.scheduledTime);
        }
      } else if (ride.timestamp) {
        if (ride.timestamp.toDate) {
          startTime = ride.timestamp.toDate();
        } else if (ride.timestamp.seconds) {
          startTime = new Date(ride.timestamp.seconds * 1000);
        } else {
          startTime = new Date(ride.timestamp);
        }
      }
      
      // Berechne endTime
      let endTime = null;
      if (ride.completedTimestamp) {
        if (ride.completedTimestamp.toDate) {
          endTime = ride.completedTimestamp.toDate();
        } else if (ride.completedTimestamp.seconds) {
          endTime = new Date(ride.completedTimestamp.seconds * 1000);
        } else {
          endTime = new Date(ride.completedTimestamp);
        }
      } else if (startTime && ride.estimatedTimeMinutes) {
        endTime = new Date(startTime.getTime() + (ride.estimatedTimeMinutes * 60000));
      }
      
      return {
        id: ride.id || ride.uid,
        type: 'ride',
        vehicleId: ride.vehicleId || `virtual-${ride.driverId}`,
        startTime: startTime,
        endTime: endTime,
        destination: ride.destination || 'Unbekannt',
        driverId: ride.driverId,
        status: ride.status,
        customerName: ride.customerName,
        orderType: ride.orderType,
        estimatedTimeMinutes: ride.estimatedTimeMinutes
      };
    }).filter(activity => 
      // Nur Aktivitäten mit gültigen Zeitangaben
      activity.startTime && activity.endTime && activity.driverId
    );
  }, []);
  
  // Automatische Daten-Updates bei Änderungen - OHNE zusätzliche Konvertierung
  useEffect(() => {
    if (isActive) {
      const timeoutId = setTimeout(() => {
        // Verwende activities direkt vom AppDataManager - sie sind bereits korrekt konvertiert!
        // convertRidesToActivities() wird NICHT benötigt, da AppDataManager bereits alle Activities
        // (Rides, Breaks, Offline) konsolidiert und korrekt formatiert.
        updateData(shiftsData, activities, vehicles);
      }, 1000);
      
      return () => clearTimeout(timeoutId);
    }
  }, [shiftsData, activities, vehicles, isActive, updateData]);
  
  // Cleanup bei Unmount
  useEffect(() => {
    return () => {
      if (isActive) {
        dispatch(stopWaitTimeManager());
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // WaitTimeManager Status für genauere Informationen - weniger frequent
  const [managerStatus, setManagerStatus] = useState(null);
  
  useEffect(() => {
    const updateManagerStatus = () => {
      const status = waitTimeManager.getStatus();
      setManagerStatus(status);
    };
    
    // Initial
    updateManagerStatus();
    
    // Nur alle 10 Sekunden aktualisieren um Performance zu verbessern
    const interval = setInterval(updateManagerStatus, 10000);
    
    return () => clearInterval(interval);
  }, []);

  return {
    // State
    waitTimes,
    nextUnavailable,
    metadata,
    isActive,
    isLoading,
    error,
    lastCalculationTime,
    
    // Actions
    startManager,
    stopManager,
    updateData,
    triggerCalculation,
    
    // Utility - verwende Manager-Daten wenn verfügbar, sonst Redux
    hasData: managerStatus?.hasData || Boolean(shiftsData),
    dataCount: managerStatus?.dataCount || {
      drivers: shiftsData?.drivers ? Object.keys(shiftsData.drivers).length : 0,
      activities: activities.length,
      vehicles: vehicles.length
    },
    managerStatus,
    
    // Export der Konvertierungsfunktion falls benötigt (z.B. für Tests)
    convertRidesToActivities
  };
};

// Hook für nur die Wartezeitdaten (für UI-Komponenten)
export const useWaitTimes = () => {
  const waitTimes = useSelector(selectWaitTimes);
  const nextUnavailable = useSelector(selectNextUnavailable);
  const metadata = useSelector(selectWaitTimeMetadata);
  const isActive = useSelector(selectIsManagerActive);
  const error = useSelector(selectWaitTimeError);
  
  return {
    waitTimes,
    nextUnavailable,
    metadata,
    isActive,
    error,
    isCalculating: !isActive || waitTimes.general === "Berechnung..."
  };
};

export default useWaitTimeManager;